/* Copyright (c) 2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, take, tap, mapTo } from 'rxjs/operators';
import { Commands } from 'bridge-commands/commands';
import { NvkvstorageTypes } from 'bridge-commands/types/common/nvkvstorage.types';
import { BridgeService } from '../bridge/bridge.service';
import { Logger, LoggingService } from '../logging/logging.service';
import { WindowService } from '../window/window.service';
import {
    AppThemes,
    AppTheme,
    NvkvstorageReadPayload,
    UserThemePreferenceKey,
    NvkvstorageWritePayload,
    SetCEFThemePayload,
    NvkvstorageDeletePayload
} from './app-theme.model';

@Injectable({
    providedIn: 'root'
})
export class AppThemeService {
    private logger: Logger;
    private currentTheme: AppTheme;
    private systemTheme: AppTheme;
    private listeningToSystemThemeChanges = false;
    private systemThemeMediaQuery;

    constructor(
        private loggingService: LoggingService,
        private bridgeService: BridgeService,
        private windowService: WindowService
    ) {
        this.logger = this.loggingService.getLogger('crimson/AppThemeService');
        this.systemThemeMediaQuery = windowService.window.matchMedia('(prefers-color-scheme: light)');
    }

    initialize(): Observable<void> {
        this.logger.info('Initializing');
        return this.fetchThemePreferenceFromStorage().pipe(
            take(1),
            map(response => {
                this.logger.info('Response from fetchThemePreference: ', response);
                if (response) {
                    if (response['value']) {
                        this.currentTheme = AppThemes.find(t => t.key === response['value']);
                        const isSystemThemeAvailable = AppThemes.some(t => t.isSystemTheme);

                        if (this.currentTheme?.isSystemTheme && isSystemThemeAvailable) {
                            this.systemTheme = this.getSystemTheme();
                            this.applyThemeToBody(this.systemTheme);
                            this.listenToSystemThemeChanges();
                        } else if (this.currentTheme) {
                            // Apply the selected theme if theme preference is not a system theme
                            this.applyThemeToBody(this.currentTheme);
                        } else {
                            // If no theme matches, then fallback to dark/default theme
                            this.currentTheme = AppThemes.find(t => t.isDefaultTheme);
                            this.applyThemeToBody(this.currentTheme);
                        }
                    } else {
                        // If no persisted theme value is returned from storage, then set currentTheme to dark/default theme
                        this.currentTheme = AppThemes.find(t => t.isDefaultTheme);
                    }
                }
            })
        );
    }

    // Applies theme preference without persisting it.
    applyAppTheme(theme?: AppTheme): void {
        const themeToApply = theme ?? AppThemes.find(appTheme => appTheme.isDefaultTheme);
        if (themeToApply?.className !== undefined && themeToApply.key) {
            this.logger.info('Setting currentTheme to ', themeToApply);
            this.currentTheme = themeToApply;

            if (themeToApply.isSystemTheme) {
                this.systemTheme = this.getSystemTheme();
                this.listenToSystemThemeChanges();
            } else if (this.listeningToSystemThemeChanges) {
                this.unsubscribeFromSystemThemeChanges();
            }

            this.applyThemeToBody(this.currentTheme.isSystemTheme ? this.systemTheme : this.currentTheme);
        } else {
            this.logger.error('theme object is invalid- ', themeToApply);
        }
    }

    // Returns the theme applied to the client as per the telemetry format.
    getThemeValToSendInTelemetry(): string {
        const currentTheme = this.getCurrentTheme();
        const currentAppliedTheme = this.getCurrentAppliedTheme();

        return currentTheme?.isSystemTheme ? `${currentTheme?.key}_${currentAppliedTheme?.key}` : currentTheme?.key;
    }

    // Saves theme preference in storage and applies the theme to body.
    saveAndSetAppTheme(theme: AppTheme): Observable<boolean> {
        // If system theme, then get OS theme and also start listening for OS theme change event
        if (theme.isSystemTheme) {
            this.systemTheme = this.getSystemTheme();
            this.listenToSystemThemeChanges();
        } else if (this.listeningToSystemThemeChanges) {
            this.unsubscribeFromSystemThemeChanges();
        }

        if (theme?.className !== undefined && theme.key) {
            this.logger.info('Setting currentTheme to ', theme);
            this.currentTheme = theme;
            const themeToApply = this.currentTheme.isSystemTheme ? this.systemTheme : this.currentTheme;

            if (this.currentTheme.isDefaultTheme) {
                return this.deleteThemePreferenceFromStorage(theme).pipe(
                    take(1),
                    tap(response => {
                        if (response) {
                            this.logger.info('Success calling  deleteThemePreferenceFromStorage: ', response);
                            this.applyThemeToBody(themeToApply);
                        }
                    }),
                    catchError(error => {
                        this.logger.error('Error deleting theme preference', error);
                        return of(false);
                    })
                );
            } else {
                return this.saveThemePreferenceInStorage(theme).pipe(
                    take(1),
                    tap(response => {
                        if (response) {
                            this.logger.info('Success calling  saveThemePreferenceInStorage: ', response);
                            this.applyThemeToBody(themeToApply);
                        }
                    }),
                    catchError(error => {
                        this.logger.error('Error saving theme preference', error);
                        return of(false);
                    })
                );
            }
        } else {
            this.logger.error('theme object is invalid- ', theme);
        }
        return of(false);
    }

    // Returns the current theme selected by the user.
    getCurrentTheme(): AppTheme {
        return this.currentTheme;
    }

    // Returns the current applied theme. If the current theme is a system theme, then it returns the theme selected at OS level.
    getCurrentAppliedTheme(): AppTheme {
        return this.getCurrentTheme()?.isSystemTheme ? this.systemTheme : this.getCurrentTheme();
    }

    // Listens to the system theme changes.
    private listenToSystemThemeChanges(): void {
        this.logger.info('inside listenToSystemThemeChanges');
        if (!this.listeningToSystemThemeChanges) {
            this.systemThemeMediaQuery.addEventListener('change', this.handleSystemThemeChange);
            this.listeningToSystemThemeChanges = true;
        }
    }

    // Unsubscribes from the system theme changes.
    private unsubscribeFromSystemThemeChanges(): void {
        this.logger.info('inside unsubscribeFromSystemThemeChanges');
        this.systemThemeMediaQuery.removeEventListener('change', this.handleSystemThemeChange);
        this.listeningToSystemThemeChanges = false;
    }

    // Saves the theme preference in storage.
    private saveThemePreferenceInStorage(theme: AppTheme): Observable<boolean> {
        const writePayload: NvkvstorageWritePayload = { key: UserThemePreferenceKey, value: theme.key };
        this.logger.info('Saving theme preference key - sending the params as', writePayload);

        return this.bridgeService.executeCommand(Commands.Common.Nvkvstorage.Write, writePayload).pipe(
            tap(response => {
                this.logger.info('Success calling Nvkvstorage.Write API');
                return response;
            }),
            mapTo(true),
            catchError(error => {
                this.logger.error('Error during Nvkvstorage.Write api call - ', error);
                return throwError(error);
            })
        );
    }

    // Deletes the theme preference in storage.
    private deleteThemePreferenceFromStorage(theme: AppTheme): Observable<boolean> {
        const deletePayload: NvkvstorageDeletePayload = { key: UserThemePreferenceKey };
        this.logger.info('Deleting theme preference key - sending the params as', deletePayload);

        return this.bridgeService.executeCommand(Commands.Common.Nvkvstorage.Delete, deletePayload).pipe(
            tap(response => {
                this.logger.info('Success calling Nvkvstorage.Delete API');
                return response;
            }),
            mapTo(true),
            catchError(error => {
                this.logger.error('Error during Nvkvstorage.Delete api call - ', error);
                return throwError(error);
            })
        );
    }

    // Fetches the theme preference from storage.
    private fetchThemePreferenceFromStorage(): Observable<NvkvstorageTypes.ReadReturn> {
        const payload: NvkvstorageReadPayload = { key: UserThemePreferenceKey };
        return this.bridgeService.executeCommand(Commands.Common.Nvkvstorage.Read, payload).pipe(
            map(response => {
                return response;
            })
        );
    }

    // Listen to theme preference change from storage.
    private listenToThemePreferenceFromStorage(): Observable<NvkvstorageTypes.ReadReturn> {
        return this.bridgeService.executeCommand(Commands.Common.Nvkvstorage.Notification).pipe(
            map(response => {
                return response;
            })
        );
    }

    // Applies the theme to the body.
    private applyThemeToBody(themeToApply: AppTheme): void {
        const bodyClassList = this.windowService.window.document.body.classList;
        AppThemes?.forEach(t => {
            if (t.className) {
                bodyClassList?.remove(t.className);
            }
        });
        this.applyThemeToCEF(themeToApply)
            .pipe(take(1))
            .subscribe(
                () => {
                    this.logger.info('success calling applyThemeToCEF');
                },
                error => {
                    this.logger.error('Failure calling applyThemeToCEF ', error);
                }
            );
        if (themeToApply.className) {
            this.logger.info('Setting body class as ', themeToApply.className);
            bodyClassList?.add(themeToApply.className);
        }
    }

    private applyThemeToCEF(theme: AppTheme): Observable<boolean> {
        const themePayload: SetCEFThemePayload = { systemTheme: theme.key };
        return this.bridgeService
            .executeCommand(Commands.Common.Applicationcontrol.SetCEFWindowTheme, themePayload)
            .pipe(
                tap(response => {
                    this.logger.info('Success calling SetCEFWindowTheme API', response);
                    return response;
                }),
                mapTo(true),
                catchError(error => {
                    this.logger.error('Error during SetCEFWindowTheme API - ', error);
                    return throwError(error);
                })
            );
    }

    // Returns true if the system theme is light.
    private isSystemLightTheme(mediaQuery?: MediaQueryList | MediaQueryListEvent): boolean {
        if (!mediaQuery) {
            mediaQuery = this.systemThemeMediaQuery;
        }
        return mediaQuery.matches;
    }

    // Returns the theme based on the theme selected at OS level.
    private getSystemTheme(mediaQuery?): AppTheme {
        const isLightTheme = this.isSystemLightTheme(mediaQuery);
        return AppThemes.find(t => t.key === (isLightTheme ? 'light' : 'dark'));
    }

    // Handles the system theme change event.
    private handleSystemThemeChange = (mediaQuery: MediaQueryList | MediaQueryListEvent) => {
        this.systemTheme = this.getSystemTheme(mediaQuery);
        this.applyThemeToBody(this.systemTheme);
        this.logger.info('System theme preference changed:', this.isSystemLightTheme(mediaQuery) ? 'light' : 'dark');
    };

    listenAndApplyTheme(): Observable<NvkvstorageTypes.ReadReturn> {
        return this.listenToThemePreferenceFromStorage().pipe(
            map(response => {
                if (response) {
                    if (response['value']) {
                        this.currentTheme = AppThemes.find(t => t.key === response['value']);
                        const isSystemThemeAvailable = AppThemes.some(t => t.isSystemTheme);

                        if (this.currentTheme?.isSystemTheme && isSystemThemeAvailable) {
                            this.systemTheme = this.getSystemTheme();
                            this.applyThemeToBody(this.systemTheme);
                            this.listenToSystemThemeChanges();
                        } else if (this.currentTheme) {
                            // Apply the selected theme if it's not a system theme
                            if (this.listeningToSystemThemeChanges) {
                                this.unsubscribeFromSystemThemeChanges();
                            }
                            this.applyThemeToBody(this.currentTheme);
                        } else {
                            // If no theme matches, then fallback to dark/default theme
                            this.currentTheme = AppThemes.find(t => t.isDefaultTheme);
                            this.applyThemeToBody(this.currentTheme);
                            if (this.currentTheme?.isSystemTheme) {
                                this.listenToSystemThemeChanges();
                            } else if (this.listeningToSystemThemeChanges) {
                                this.unsubscribeFromSystemThemeChanges();
                            }
                        }
                    } else {
                        // If no value, then fallback to dark/default theme
                        this.currentTheme = AppThemes.find(t => t.isDefaultTheme);
                        this.applyThemeToBody(this.currentTheme);
                        if (this.currentTheme?.isSystemTheme) {
                            this.listenToSystemThemeChanges();
                        } else if (this.listeningToSystemThemeChanges) {
                            this.unsubscribeFromSystemThemeChanges();
                        }
                    }
                }
                return response;
            }),
            catchError(error => {
                this.logger.error('Error during listenAndApplyTheme : ', error);
                return throwError(error);
            })
        );
    }
}
